import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import { auth, db } from "./firebaseConfig";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Firebase Auth user
  const [userProfile, setUserProfile] = useState(null); // Firestore profile
  const [authLoading, setAuthLoading] = useState(true);

  // Local/Session storage state for pending registration
  const [pendingUser, setPendingUser] = useState(() => {
    try {
      const saved = localStorage.getItem("pendingRegistration");
      return saved ? JSON.parse(saved) : null;
    } catch (err) {
      return null;
    }
  });

  const clearPendingStatus = useCallback(() => {
    try {
      localStorage.removeItem("pendingRegistration");
    } catch (e) {}
    setPendingUser(null);
  }, []);

  const savePendingStatus = useCallback((data) => {
    try {
      localStorage.setItem("pendingRegistration", JSON.stringify(data));
    } catch (e) {}
    setPendingUser(data);
  }, []);

  // Derived state
  const isLoggedIn = !!user && !!userProfile;
  const isApproved = userProfile?.status === "approved";
  const isPending = userProfile?.status === "pending";
  const isRejected = userProfile?.status === "rejected";

  // Fetch user profile from Firestore
  const fetchProfile = useCallback(async (uid) => {
    try {
      const docRef = doc(db, "userRegistrations", uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (err) {
      console.error("Error fetching user profile:", err);
      return null;
    }
  }, []);

  // Listen to auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const profile = await fetchProfile(firebaseUser.uid);
        setUserProfile(profile);
        if (profile?.status === "approved") {
          clearPendingStatus();
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [fetchProfile, clearPendingStatus]);

  // Register new user
  const register = async ({ name, email, phone, company, city, password }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const uid = cred.user.uid;

    const profileData = {
      uid,
      name,
      email,
      phone,
      company,
      city,
      status: "pending",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Create Firestore profile
    await setDoc(doc(db, "userRegistrations", uid), profileData);

    // Save pending registration in LocalStorage
    savePendingStatus({
      email,
      name,
      company,
      status: "pending",
      registeredAt: new Date().toISOString(),
    });

    // Sign out immediately — they can't use the app until approved
    await signOut(auth);

    return { success: true };
  };

  // Login
  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const profile = await fetchProfile(cred.user.uid);
    setUserProfile(profile);

    if (!profile || profile.role === "admin" || profile.isAdmin || cred.user.email.toLowerCase().includes("admin")) {
      // No registration found or admin account — let it through to admin dashboard
      clearPendingStatus();
      return { success: true, isAdmin: true };
    }

    if (profile.status === "approved") {
      clearPendingStatus();
      return { success: true, status: "approved" };
    } else if (profile.status === "pending") {
      savePendingStatus({
        email,
        name: profile.name || "",
        company: profile.company || "",
        status: "pending",
        updatedAt: new Date().toISOString(),
      });
      await signOut(auth);
      return { success: false, status: "pending" };
    } else if (profile.status === "rejected") {
      savePendingStatus({
        email,
        name: profile.name || "",
        status: "rejected",
        updatedAt: new Date().toISOString(),
      });
      await signOut(auth);
      return { success: false, status: "rejected" };
    }

    return { success: false, status: profile.status };
  };

  // Logout
  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setUserProfile(null);
  };

  // Refresh profile (useful after admin changes status)
  const refreshProfile = useCallback(async () => {
    if (user) {
      const profile = await fetchProfile(user.uid);
      setUserProfile(profile);
      if (profile?.status === "approved") {
        clearPendingStatus();
      }
    }
  }, [user, fetchProfile, clearPendingStatus]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        pendingUser,
        isLoggedIn,
        isApproved,
        isPending,
        isRejected,
        authLoading,
        register,
        login,
        logout,
        refreshProfile,
        clearPendingStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
